import { useState } from 'react';
import useDrivePicker from 'react-google-drive-picker';

interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
}

interface PickerCallbackWithToken {
  action: string;
  docs: GoogleDriveFile[];
  oauthToken?: string;
}

interface UseGoogleDrivePickerResult {
  googleDriveFiles: GoogleDriveFile[];
  accessToken: string;
  handleOpenPicker: () => void;
  downloadFiles: () => Promise<File[]>;
}

export const useGoogleDrivePicker = (): UseGoogleDrivePickerResult => {
  const [googleDriveFiles, setGoogleDriveFiles] = useState<GoogleDriveFile[]>([]);
  const [accessToken, setAccessToken] = useState<string>('');
  const [openPicker] = useDrivePicker();

  const handleOpenPicker = () => {
    openPicker({
      clientId: process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID || '',
      developerKey: process.env.REACT_APP_GOOGLE_DRIVE_DEVELOPER_KEY || '',
      viewId: 'DOCS',
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: true,
      setIncludeFolders: true,
      callbackFunction: (data: PickerCallbackWithToken) => {
        if (data.action === 'picked') {
          setGoogleDriveFiles(data.docs);
          setAccessToken(data.oauthToken || '');
        }
      },
    });
  };

  const downloadFiles = async (): Promise<File[]> => {
    const filePromises = googleDriveFiles.map(async gFile => {
      const fileId = gFile.id;
      const downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;

      const response = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const blob = await response.blob();
      return new File([blob], gFile.name, { type: gFile.mimeType });
    });

    return Promise.all(filePromises);
  };

  return {
    googleDriveFiles,
    accessToken,
    handleOpenPicker,
    downloadFiles,
  };
};
